import { useState, useEffect, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { Pencil, Save, Check, X } from 'lucide-react'
import UiButton from '../ui/UiButton'
import ShippingAddressForm, { normalizeAddress, formatAddress } from '../ShippingAddressForm'
import PhoneInput, { isValidPhone } from '../PhoneInput'

function Label({ children }) {
  return (
    <label className="block text-base font-bold text-brand-blue mb-1">
      {children}
    </label>
  )
}

function Input(props) {
  return (
    <input
      {...props}
      className="w-full bg-[var(--library-input-bg)] rounded-xl px-4 py-2 text-base text-brand-blue placeholder-brand-blue/50 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-gold disabled:opacity-60"
    />
  )
}

function ReadOnlyField({ label, value }) {
  return (
    <div>
      <p className="text-base font-bold text-brand-blue mb-1">{label}</p>
      <p className="text-base text-brand-blue/80">{value || '—'}</p>
    </div>
  )
}

function isValidEmail(email) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)
}

function SectionCard({ title, section, children, editingSection, setEditingSection, saving, disableSave, handleSave, handleCancel, t }) {
  const isEditing = editingSection === section

  return (
    <div className="bg-[var(--library-input-bg)] rounded-2xl p-5 border border-[var(--library-border)]">
      <div className="flex items-center justify-between gap-4 mb-4">
        <h3 className="font-climate text-xl text-brand-gold">{title}</h3>
        {!isEditing && (
          <button
            type="button"
            onClick={() => setEditingSection(section)}
            className="flex items-center gap-2 text-brand-blue hover:text-brand-orange transition font-bold text-sm"
          >
            <Pencil size={16} />
            {t('library.profile.edit')}
          </button>
        )}
      </div>

      <div className="space-y-4">{children}</div>

      {isEditing && (
        <div className="flex flex-wrap gap-3 mt-6">
          <UiButton
            type="button"
            variant="gold"
            size="sm"
            disabled={saving || disableSave}
            icon={Save}
            onClick={() => handleSave(section)}
          >
            {saving ? t('common.loading') : t('library.profile.save')}
          </UiButton>
          <UiButton
            type="button"
            variant="secondary"
            size="sm"
            disabled={saving}
            icon={X}
            onClick={handleCancel}
          >
            {t('library.profile.cancel')}
          </UiButton>
        </div>
      )}
    </div>
  )
}

/**
 * Profile form with read-only sections and per-section editing.
 *
 * Props:
 * - profile: current profile object
 * - saving: whether a save is in progress
 * - onSave: async (updates) => void
 */
export default function ProfileForm({ profile, saving, onSave }) {
  const { t } = useTranslation()
  const [saved, setSaved] = useState(false)
  const [editingSection, setEditingSection] = useState(null)
  const [emailError, setEmailError] = useState('')

  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phoneNumber: '',
    shippingAddress: normalizeAddress(),
  })

  useEffect(() => {
    if (!profile) return
    setForm({
      firstName: profile.firstName || '',
      lastName: profile.lastName || '',
      email: profile.email || '',
      phoneNumber: profile.phoneNumber || '',
      shippingAddress: normalizeAddress(profile.shippingAddress),
    })
  }, [profile])

  const [phoneError, setPhoneError] = useState('')

  const handleChange = useCallback((field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }))
    if (field === 'email') {
      setEmailError(value && !isValidEmail(value) ? t('library.profile.emailInvalid') : '')
    }
    if (field === 'phoneNumber') {
      setPhoneError(value && !isValidPhone(value) ? t('library.profile.phoneInvalid') : '')
    }
  }, [t])

  const handleAddressChange = useCallback((nextAddress) => {
    setForm((prev) => ({
      ...prev,
      shippingAddress: normalizeAddress(nextAddress),
    }))
  }, [])

  const handleSave = async (section) => {
    if (section === 'personal') {
      if (!isValidEmail(form.email)) {
        setEmailError(t('library.profile.emailInvalid'))
        return
      }
      if (form.phoneNumber && !isValidPhone(form.phoneNumber)) {
        setPhoneError(t('library.profile.phoneInvalid'))
        return
      }
    }

    let updates = {}
    if (section === 'personal') {
      updates = {
        firstName: form.firstName,
        lastName: form.lastName,
        email: form.email,
        phoneNumber: form.phoneNumber,
      }
    } else if (section === 'shippingAddress') {
      updates = { shippingAddress: form.shippingAddress }
    }

    await onSave(updates)
    setEditingSection(null)
    setSaved(true)
    setTimeout(() => setSaved(false), 4000)
  }

  const handleCancel = () => {
    setEditingSection(null)
    setEmailError('')
    setPhoneError('')
    // Reset form to current profile values.
    if (profile) {
      setForm({
        firstName: profile.firstName || '',
        lastName: profile.lastName || '',
        email: profile.email || '',
        phoneNumber: profile.phoneNumber || '',
        shippingAddress: normalizeAddress(profile.shippingAddress),
      })
    }
  }

  return (
    <form onSubmit={(e) => e.preventDefault()} className="space-y-6">
      {saved && (
        <div className="flex items-center gap-2 bg-green-100 text-green-800 rounded-xl px-4 py-3">
          <Check size={20} />
          <span className="font-bold">{t('library.profile.saved')}</span>
        </div>
      )}

      {/* Dane osobowe */}
      <SectionCard
        title={t('library.profile.section.personal')}
        section="personal"
        editingSection={editingSection}
        setEditingSection={setEditingSection}
        saving={saving}
        disableSave={!!emailError || !!phoneError}
        handleSave={handleSave}
        handleCancel={handleCancel}
        t={t}
      >
        {editingSection === 'personal' ? (
          <>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label>{t('library.profile.firstName')}</Label>
                <Input
                  type="text"
                  value={form.firstName}
                  onChange={(e) => handleChange('firstName', e.target.value)}
                  required
                />
              </div>
              <div>
                <Label>{t('library.profile.lastName')}</Label>
                <Input
                  type="text"
                  value={form.lastName}
                  onChange={(e) => handleChange('lastName', e.target.value)}
                  required
                />
              </div>
            </div>
            <div>
              <Label>{t('library.profile.email')}</Label>
              <Input
                type="email"
                value={form.email}
                onChange={(e) => handleChange('email', e.target.value)}
                required
              />
              {emailError && (
                <p className="text-sm text-red-600 mt-1">{emailError}</p>
              )}
              {form.email !== (profile?.email || '') && !emailError && (
                <p className="text-sm text-brand-blue/70 mt-1">
                  {t('library.profile.emailChangeNotice')}
                </p>
              )}
            </div>
            <div>
              <PhoneInput
                label={t('library.profile.phoneNumber')}
                value={form.phoneNumber}
                onChange={(value) => handleChange('phoneNumber', value)}
                placeholder={t('library.profile.phonePlaceholder')}
                disabled={saving}
                showValidation={editingSection === 'personal'}
              />
            </div>
          </>
        ) : (
          <>
            <div className="grid md:grid-cols-2 gap-4">
              <ReadOnlyField label={t('library.profile.firstName')} value={form.firstName} />
              <ReadOnlyField label={t('library.profile.lastName')} value={form.lastName} />
            </div>
            <ReadOnlyField label={t('library.profile.email')} value={form.email} />
            <ReadOnlyField label={t('library.profile.phoneNumber')} value={form.phoneNumber} />
          </>
        )}
      </SectionCard>

      {/* Adres dostawy */}
      <SectionCard
        title={t('library.profile.section.shippingAddress')}
        section="shippingAddress"
        editingSection={editingSection}
        setEditingSection={setEditingSection}
        saving={saving}
        disableSave={false}
        handleSave={handleSave}
        handleCancel={handleCancel}
        t={t}
      >
        {editingSection === 'shippingAddress' ? (
          <ShippingAddressForm
            value={form.shippingAddress}
            onChange={handleAddressChange}
            disabled={saving}
          />
        ) : (
          <div>
            <p className="text-base font-bold text-brand-blue mb-1">
              {form.shippingAddress.type === 'paczkomat'
                ? t('checkout.address.paczkomat', 'Paczkomat')
                : t('checkout.address.home', 'Adres domowy')}
            </p>
            <p className="text-base text-brand-blue/80">{formatAddress(form.shippingAddress)}</p>
          </div>
        )}
      </SectionCard>

      {/* Dane płatności */}
      <div className="bg-[var(--library-input-bg)] rounded-2xl p-5 border border-[var(--library-border)]">
        <h3 className="font-climate text-xl text-brand-gold mb-4">
          {t('library.profile.section.paymentDetails')}
        </h3>
        <div className="space-y-4">
          <ReadOnlyField
            label={t('library.profile.payment.provider')}
            value="Stripe"
          />
          <p className="text-base text-brand-blue/70">
            {t('library.profile.payment.info')}
          </p>
        </div>
      </div>
    </form>
  )
}
